import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  CogIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from "@heroicons/react/24/outline";

export const ProjectSettingsTab = ({ project, onRefresh }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    retentionPercentage: 10,
    finalInvoiceDueDays: 60
  });
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null, saving, success, error

  useEffect(() => {
    // TODO: Fetch project settings from API
    const mockData = {
      retentionPercentage: project.retention_percentage || 10,
      finalInvoiceDueDays: project.final_invoice_due_days || 60
    };
    
    setFormData(mockData);
  }, [project.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaveStatus("saving");
    
    try {
      // TODO: Implement API call to save project settings
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      setSaveStatus("success");
      if (onRefresh) {
        onRefresh();
      }
      
      // Reset success status after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t("project.settings")}
        </h3>
        <div className="flex items-center space-x-2">
          {saveStatus === "success" && (
            <div className="flex items-center text-green-600">
              <CheckCircleIcon className="h-5 w-5 mr-1" />
              <span className="text-sm">Lagret</span>
            </div>
          )}
          {saveStatus === "error" && (
            <div className="flex items-center text-red-600">
              <ExclamationTriangleIcon className="h-5 w-5 mr-1" />
              <span className="text-sm">Feil ved lagring</span>
            </div>
          )}
        </div>
      </div>

      {/* Settings Form */}
      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Final Invoice Due Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("project.settings.final_invoice_due_days")}
            </label>
            <div className="relative">
              <input
                type="number"
                min="1"
                max="365"
                value={formData.finalInvoiceDueDays}
                onChange={(e) => handleInputChange("finalInvoiceDueDays", parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="60"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">dager</span>
              </div>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {t("project.settings.default_due_days")}
            </p>
          </div>

          {/* Additional Settings */}
          {/* <div className="border-t border-gray-200 pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">
              Tilleggsinnstillinger
            </h4>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="enableTripletexSync"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="enableTripletexSync" className="ml-2 block text-sm text-gray-900">
                  Aktiver Tripletex synkronisering
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  id="autoCreateInvoices"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="autoCreateInvoices" className="ml-2 block text-sm text-gray-900">
                  Automatisk opprettelse av A-konto fakturaer
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  id="notifyOnOverdue"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="notifyOnOverdue" className="ml-2 block text-sm text-gray-900">
                  Varsle ved forfalte fakturaer
                </label>
              </div>
            </div>
          </div> */}

          {/* Save Button */}
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Lagrer...
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Lagre innstillinger
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Project Information */}
      <div className="bg-white shadow rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">
          Prosjektinformasjon
        </h4>
        
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Prosjektnummer</dt>
            <dd className="mt-1 text-sm text-gray-900">{project.project_number || "Ikke satt"}</dd>
          </div>
          
          <div>
            <dt className="text-sm font-medium text-gray-500">Prosjektnavn</dt>
            <dd className="mt-1 text-sm text-gray-900">{project.name || "Ikke satt"}</dd>
          </div>
          
          <div>
            <dt className="text-sm font-medium text-gray-500">Kunde</dt>
            <dd className="mt-1 text-sm text-gray-900">{project.customer?.name || "Ikke satt"}</dd>
          </div>
          
          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1 text-sm text-gray-900">{project.status || "Ikke satt"}</dd>
          </div>
          
          <div>
            <dt className="text-sm font-medium text-gray-500">Opprettet</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {project.created_at ? new Date(project.created_at).toLocaleDateString('no-NO') : "Ikke satt"}
            </dd>
          </div>
          
          <div>
            <dt className="text-sm font-medium text-gray-500">Sist oppdatert</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {project.updated_at ? new Date(project.updated_at).toLocaleDateString('no-NO') : "Ikke satt"}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
};
